import { useEffect } from 'react'
import { Sparkles } from 'lucide-react'
import { useChangelog } from '@/hooks/use-changelog'
import { formatChangelogDate, type ChangelogTag } from '@/lib/changelog'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { captureEvent } from '@/lib/analytics'

const TAG_VARIANT: Record<ChangelogTag, 'default' | 'success' | 'warning'> = {
  New: 'default',
  Improved: 'success',
  Fixed: 'warning',
}

export function ChangelogPage() {
  const { entries, markSeen } = useChangelog()

  // Viewing the changelog counts as "seen" — clears the footer dot.
  useEffect(() => {
    captureEvent('changelog_viewed')
    markSeen()
  }, [markSeen])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Sparkles className="h-6 w-6 text-primary" />
          What’s New
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Updates and improvements we’ve shipped to Rundown.
        </p>
      </div>

      <div className="space-y-4">
        {entries.map((entry) => {
          const Icon = entry.icon
          return (
            <Card key={entry.id} className="p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Badge variant={TAG_VARIANT[entry.tag]}>{entry.tag}</Badge>
                  <h2 className="flex items-center gap-2 text-base font-semibold">
                    {Icon && <Icon className="h-4 w-4 text-primary" />}
                    {entry.title}
                  </h2>
                </div>
                <time className="shrink-0 text-xs text-muted-foreground" dateTime={entry.date}>
                  {formatChangelogDate(entry.date)}
                </time>
              </div>

              <p className="mt-2 text-sm text-foreground/80">{entry.summary}</p>

              {entry.details && entry.details.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {entry.details.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/60" />
                      {d}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}
